import React, { useEffect, useState } from "react";
import { growthApi } from "../api";
import { Badge } from "./ui/badge";
import { BarChart3, Link2, Globe } from "lucide-react";

const FUNNEL_STEPS = [
  { key: "county_page_visit", label: "County / Service Page Visits" },
  { key: "family_caregiver_visit", label: "Family Caregiver Page Visits" },
  { key: "call_click", label: "Call Clicks" },
  { key: "care_request_submitted", label: "Care Requests Submitted" },
  { key: "referral_submitted", label: "Referrals Submitted" },
  { key: "coverage_verification", label: "Coverage Verifications" },
  { key: "family_caregiver_inquiry", label: "Family Caregiver Inquiries" },
  { key: "caregiver_application", label: "Caregiver Applications" },
];

const GrowthFunnel = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    growthApi.funnel().then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-center text-slate-400 py-10">Loading funnel…</div>;

  const max = Math.max(1, ...FUNNEL_STEPS.map((s) => data.byType[s.key] || 0));

  return (
    <div data-testid="growth-funnel" className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <h3 className="font-display font-bold text-[#0E2A33] flex items-center gap-2 mb-1">
            <BarChart3 className="h-5 w-5 text-[#0E5A72]" /> Conversion Funnel
          </h3>
          <p className="text-sm text-slate-500 mb-4">Primary KPI: qualified client opportunities — not page views.</p>
          <div className="space-y-3">
            {FUNNEL_STEPS.map((s) => {
              const n = data.byType[s.key] || 0;
              return (
                <div key={s.key}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-slate-700">{s.label}</span>
                    <span className="font-semibold text-[#0E5A72]">{n}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-[#2E9E6A]" style={{ width: `${(n / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3 text-center">
            <div>
              <div className="font-display font-extrabold text-2xl text-[#0E5A72]">{data.familyCaregiverTotal}</div>
              <div className="text-xs text-slate-500">Family caregiver inquiries</div>
            </div>
            <div>
              <div className="font-display font-extrabold text-2xl text-[#2E9E6A]">{data.familyCaregiverReady}</div>
              <div className="text-xs text-slate-500">Ready to start</div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h3 className="font-display font-bold text-[#0E2A33] mb-3">Activity by County (top 20)</h3>
            <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
              {data.byCounty.map((c) => (
                <div key={c.county} className="flex items-center justify-between text-sm rounded-lg border border-slate-100 px-3 py-1.5">
                  <span className="text-slate-700">{c.county}</span>
                  <Badge className="border bg-[#E9F6EF] text-[#23875A] border-emerald-200 text-[11px]">{c.events} events</Badge>
                </div>
              ))}
              {data.byCounty.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">No county activity tracked yet.</div>}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h3 className="font-display font-bold text-[#0E2A33] flex items-center gap-2 mb-2">
              <Globe className="h-5 w-5 text-[#0E5A72]" /> Google Search Console
            </h3>
            <p className="text-sm text-slate-500">
              Not connected yet. Once connected, query impressions, clicks, CTR and average position will feed the
              Content Opportunities list (positions 5–20, high-impression / low-click queries).
            </p>
            <div className="mt-3 text-sm text-slate-600 bg-[#F7FBFC] rounded-xl border border-slate-100 p-4">
              <div className="font-semibold text-[#0E2A33] mb-1 flex items-center gap-2"><Link2 className="h-4 w-4" /> Setup steps</div>
              1. Go to search.google.com/search-console and add property <b>myohcare.com</b><br />
              2. Verify via your Squarespace DNS (TXT record)<br />
              3. Submit sitemaps: <b>myohcare.com/sitemap.xml</b> and <b>myohcare.com/api/sitemap.xml</b><br />
              4. Ask me to connect the GSC API when you're ready
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrowthFunnel;
